<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="note-spese-vehicles">
    <table v-if="vehicles.length > 0" class="note-spese-vehicles-table">
      <thead>
        <tr>
          <th>{{ $t('noteSpese.vehicle_description') }}</th>
          <th>{{ $t('noteSpese.vehicle_plate') }}</th>
          <th class="note-spese-numeric">{{ $t('noteSpese.rate_per_km') }}</th>
          <th>{{ $t('noteSpese.director_active') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="vehicle in vehicles" :key="vehicle.id">
          <tr>
            <td>{{ vehicle.description }}</td>
            <td>{{ vehicle.plate }}</td>
            <td class="note-spese-numeric">{{ vehicle.currentRate ?? '-' }}</td>
            <td>
              <oxd-input-field
                type="checkbox"
                :model-value="vehicle.active"
                @update:model-value="onToggleVehicleActive(vehicle, $event)"
              />
            </td>
            <td>
              <oxd-button
                display-type="ghost"
                :label="$t('noteSpese.rates')"
                @click="onToggleRates(vehicle)"
              />
            </td>
          </tr>
          <tr v-if="expandedVehicleId === vehicle.id">
            <td colspan="5" class="note-spese-rates-cell">
              <table v-if="rates.length > 0" class="note-spese-rates-table">
                <thead>
                  <tr>
                    <th>{{ $t('noteSpese.rate_valid_from') }}</th>
                    <th class="note-spese-numeric">
                      {{ $t('noteSpese.rate_per_km') }}
                    </th>
                    <th>{{ $t('noteSpese.rate_source') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rate in rates" :key="rate.id">
                    <td>{{ rate.validFrom }}</td>
                    <td class="note-spese-numeric">{{ rate.ratePerKm }}</td>
                    <td>{{ rate.source }}</td>
                    <td>
                      <oxd-icon-button
                        name="trash"
                        @click="onDeleteRate(rate)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <oxd-text v-else>{{ $t('general.no_records_found') }}</oxd-text>

              <oxd-form :loading="isAddingRate" @submit-valid="onAddRate">
                <oxd-form-row>
                  <oxd-grid :cols="3" class="orangehrm-full-width-grid">
                    <oxd-grid-item>
                      <oxd-input-field
                        v-model="newRate.validFrom"
                        type="date"
                        :label="$t('noteSpese.rate_valid_from')"
                        :rules="rateRules.validFrom"
                        required
                      />
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <oxd-input-field
                        v-model="newRate.ratePerKm"
                        :label="$t('noteSpese.rate_per_km')"
                        :rules="rateRules.ratePerKm"
                        required
                      />
                    </oxd-grid-item>
                    <oxd-grid-item>
                      <oxd-input-field
                        v-model="newRate.source"
                        :label="$t('noteSpese.rate_source')"
                      />
                    </oxd-grid-item>
                  </oxd-grid>
                </oxd-form-row>
                <oxd-form-actions>
                  <oxd-button
                    type="submit"
                    display-type="secondary"
                    :label="$t('noteSpese.add_rate')"
                  />
                </oxd-form-actions>
              </oxd-form>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <oxd-text v-else>{{ $t('general.no_records_found') }}</oxd-text>

    <oxd-divider />

    <oxd-form :loading="isAddingVehicle" @submit-valid="onAddVehicle">
      <oxd-form-row>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="newVehicle.description"
              :label="$t('noteSpese.vehicle_description')"
              :rules="vehicleRules.description"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="newVehicle.plate"
              :label="$t('noteSpese.vehicle_plate')"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-actions>
        <oxd-button
          type="submit"
          display-type="secondary"
          :label="$t('noteSpese.add_vehicle')"
        />
      </oxd-form-actions>
    </oxd-form>

    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';
import {
  required,
  digitsOnlyWithDecimalPoint,
} from '@/core/util/validation/rules';
import DeleteConfirmationDialog from '@/core/components/dialogs/DeleteConfirmationDialog.vue';

export default {
  name: 'DirectorVehicles',

  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },

  props: {
    empNumber: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    const toast = useToast();

    const vehiclesHttp = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/note-spese/directors/${props.empNumber}/vehicles`,
    );
    const vehicleHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/vehicles',
    );
    const ratesByVehicleHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/vehicles',
    );
    const rateHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/note-spese/rates',
    );

    return {
      toast,
      vehiclesHttp,
      vehicleHttp,
      ratesByVehicleHttp,
      rateHttp,
    };
  },

  data() {
    return {
      vehicles: [],
      expandedVehicleId: null,
      rates: [],
      rateToDelete: null,
      isAddingVehicle: false,
      isAddingRate: false,
      newVehicle: {description: '', plate: ''},
      newRate: {validFrom: '', ratePerKm: '', source: ''},
    };
  },

  computed: {
    vehicleRules() {
      return {
        description: [required],
      };
    },

    rateRules() {
      return {
        validFrom: [required],
        ratePerKm: [required, digitsOnlyWithDecimalPoint],
      };
    },
  },

  created() {
    this.fetchVehicles();
  },

  methods: {
    async fetchVehicles() {
      const response = await this.vehiclesHttp.getAll();
      this.vehicles = response.data?.data ?? [];
    },

    async fetchRates(vehicleId) {
      this.ratesByVehicleHttp.apiSection = `/api/v2/note-spese/vehicles/${vehicleId}/rates`;
      const response = await this.ratesByVehicleHttp.getAll();
      this.rates = response.data?.data ?? [];
    },

    async onToggleVehicleActive(vehicle, active) {
      await this.vehicleHttp.update(vehicle.id, {
        description: vehicle.description,
        plate: vehicle.plate,
        active,
      });
      vehicle.active = active;
      this.toast.updateSuccess();
    },

    async onToggleRates(vehicle) {
      if (this.expandedVehicleId === vehicle.id) {
        this.expandedVehicleId = null;
        this.rates = [];
        return;
      }
      this.expandedVehicleId = vehicle.id;
      await this.fetchRates(vehicle.id);
    },

    async onAddVehicle() {
      this.isAddingVehicle = true;
      try {
        await this.vehiclesHttp.create({
          description: this.newVehicle.description,
          plate: this.newVehicle.plate,
        });
        this.newVehicle = {description: '', plate: ''};
        this.toast.addSuccess();
        await this.fetchVehicles();
      } finally {
        this.isAddingVehicle = false;
      }
    },

    async onAddRate() {
      this.isAddingRate = true;
      try {
        this.ratesByVehicleHttp.apiSection = `/api/v2/note-spese/vehicles/${this.expandedVehicleId}/rates`;
        await this.ratesByVehicleHttp.create({
          validFrom: this.newRate.validFrom,
          ratePerKm: this.newRate.ratePerKm,
          source: this.newRate.source,
        });
        this.newRate = {validFrom: '', ratePerKm: '', source: ''};
        this.toast.addSuccess();
        await this.fetchRates(this.expandedVehicleId);
        await this.fetchVehicles();
      } finally {
        this.isAddingRate = false;
      }
    },

    onDeleteRate(rate) {
      this.rateToDelete = rate;
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.confirmDeleteRate();
        }
      });
    },

    async confirmDeleteRate() {
      if (!this.rateToDelete) return;
      await this.rateHttp.delete(this.rateToDelete.id);
      this.toast.deleteSuccess();
      this.rateToDelete = null;
      await this.fetchRates(this.expandedVehicleId);
      await this.fetchVehicles();
    },
  },
};
</script>

<style lang="scss" scoped>
.note-spese-vehicles {
  padding: 0.5rem 0;
}

.note-spese-vehicles-table,
.note-spese-rates-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.5rem;
    border-bottom: 1px solid #e0e0e0;
  }
}

.note-spese-numeric {
  text-align: right;
}

.note-spese-rates-cell {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
